import { includes } from 'lodash-es';
import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { graphql } from '__generated__';

import type { RootState } from 'store';

import { useBoundAuthActions } from 'common/auth/hooks/useBoundAuthActions';
import { nonAuthClient } from 'common/graphql/client';
import { CognitoErrorMessage } from 'common/graphql/types';
import { AuditCreationEventsEnum, AuditEntityIdEnum } from 'common/log/auditevents';
import { CIAuditLogger } from 'common/log/ci.auditlogger';
import { CILogger } from 'common/log/log.provider';
import { CIPageStatus } from 'common/viewcomp/general/ci_pagestatus';

export const DEFAULT_PATH = '/';

export const useHandleAuthError = ({
    onLockOut,
    onErrorHandleFinish,
    username,
}: {
    onLockOut?: () => void;
    onErrorHandleFinish?: () => void;
    username?: string;
}): void => {
    const { t } = useTranslation('auth');
    const auth = useSelector((state: RootState) => state.auth);

    const authActions = useBoundAuthActions();

    const clearErrorState = useCallback((): void => {
        authActions.clearError();
    }, [authActions]);

    useEffect(() => {
        const currentAuthError = auth.error;
        let errorMessage = currentAuthError?.translationKey;

        if (!errorMessage) {
            return;
        }

        const attemptedUsername = username ?? '';

        // Also might make sense to remove the login from Redux logic, as it cannot be awaited -> or find a better way to handle this
        // handle no. of attempts and lock out
        if (errorMessage === 'cognito.NotAuthorizedException') {
            let attemptsRemaining: number | null = null;

            nonAuthClient
                .query({
                    query: GET_REMAINING_ATTEMPTS,
                    variables: {
                        username: attemptedUsername,
                    },
                })
                .then((res): void => {
                    attemptsRemaining = res.data.getUserNumberOfLoginTriesBeforeLockout;
                })
                .catch((err): void => {
                    CILogger.getInstance().error('Failed to retrieve remaining login attempts', err);
                })
                .finally((): void => {
                    onErrorHandleFinish?.();

                    const isLockedOut = attemptsRemaining === 0;

                    if (currentAuthError?.message === CognitoErrorMessage.UserDisabled) {
                        errorMessage = 'cognito.UserDisabledException';
                    } else {
                        errorMessage = isLockedOut
                            ? 'cognito.UserLambdaValidationException'
                            : currentAuthError?.translationKey;
                    }

                    CIPageStatus.error(t(errorMessage), null, clearErrorState);

                    if (isLockedOut) {
                        onLockOut?.();

                        CILogger.getInstance().info({
                            // kibana logging
                            message: `${attemptedUsername} was locked out for 10 minutes.`,
                            username: attemptedUsername,
                        });

                        // Added for audit log event when the user locked out
                        CIAuditLogger.getInstance().log(
                            AuditCreationEventsEnum.Logout,
                            `User ${attemptedUsername} was locked out of their account`,
                            null,
                            {
                                relatedEntityName: AuditEntityIdEnum.User,
                            }
                        );
                    }
                });

            return;
        }

        if (
            currentAuthError?.translationKey === 'cognito.UserLambdaValidationException' &&
            includes(currentAuthError?.message, 'PasswordExpired')
        ) {
            errorMessage = 'cognito.PasswordExpired';
        }

        onErrorHandleFinish?.();
        CIPageStatus.error(t(errorMessage), null, clearErrorState);
    }, [auth.error, clearErrorState, username, t, onErrorHandleFinish, onLockOut]);
};

const GET_REMAINING_ATTEMPTS = graphql(`
    query LoginGetUserNumberOfLoginTriesBeforeLockout($username: String!) {
        getUserNumberOfLoginTriesBeforeLockout(username: $username)
    }
`);

export const useResetAuth = (): void => {
    const authActions = useBoundAuthActions();

    useEffect(() => {
        authActions.resetComplete();
    }, [authActions]);
};
